<template>
  <div v-for="note in notes" :key="note.id" class="mb-2 card">
    <div class="card-body">
      <button
        v-if="note.can_edit"
        class="note-delete"
        @click="noteToDelete = note"
      ></button>
      <div>{{ note.notes }}</div>
    </div>
    <div class="card-footer text-body-secondary d-flex align-items-start gap-3">
      <span>By {{ note.writer_name }}</span>
      <span>
        <i class="bi bi-clock-history"></i>
        {{ formatDate(note.updated) }}
      </span>
    </div>
  </div>
  <textarea
    v-model="draftNotes"
    class="form-control"
    placeholder="Write visit notes here"
  ></textarea>
  <div v-if="draftNotes" class="text-end mt-1">
    <button class="btn btn-sm btn-secondary me-1" @click="draftNotes = ''">
      Cancel
    </button>
    <button class="btn btn-sm btn-primary" @click="addNoteClicked">Save</button>
  </div>
  <ModalForm
    v-show="!!noteToDelete"
    title="Delete note?"
    @close="closeDeleteNoteModal()"
  >
    <blockquote>{{ noteToDelete?.notes }}</blockquote>
    <p class="text-danger">Are you sure you want to delete this note?</p>
    <div class="text-center mt-5">
      <button class="btn btn-secondary me-3" @click="closeDeleteNoteModal()">
        Cancel
      </button>
      <button
        class="btn btn-primary"
        @click="noteToDelete && confirmDeleteNote(noteToDelete.id)"
      >
        Delete
      </button>
    </div>
  </ModalForm>
</template>
<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import backend from "@/backend";
import { formatDate } from "@/util/formatting";
import ModalForm from "@/components/ModalForm.vue";
import type { Optional, VisitNote } from "@/types";

interface Props {
  visitID?: number;
}

const props = defineProps<Props>();
const notes = ref<VisitNote[]>([]);
const draftNotes = ref<string>("");
const noteToDelete = ref<Optional<VisitNote>>(null);

const emit = defineEmits<{
  (e: "change", value: string): void;
}>();

watch(draftNotes, (value) => {
  emit("change", value);
});

async function loadNotes(): Promise<void> {
  notes.value = [];
  if (props?.visitID) {
    notes.value = await backend.visits.getNotes(props.visitID);
  } else {
    notes.value = [];
  }
}

async function addNoteClicked(): Promise<void> {
  await backend.visits.addNote(props.visitID!, draftNotes.value);
  draftNotes.value = "";
  loadNotes();
}

async function confirmDeleteNote(noteId: number): Promise<void> {
  if (!noteToDelete.value) {
    throw Error("Attempting to delete visit notes without selecting one.");
  }
  await backend.visits.deleteNote(noteId);
  noteToDelete.value = null;
  loadNotes();
}

function closeDeleteNoteModal(): void {
  noteToDelete.value = null;
}

onMounted(loadNotes);
watch(props, loadNotes);
</script>
